// src/context/TaskContext.js
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { api } from 'services/api';
import { useAuth } from './AuthContext';
import { devWarn, devError } from 'utils/devLog';

const TaskContext = createContext();
export const useTasks = () => useContext(TaskContext);

export const TaskProvider = ({ children }) => {
  const { currentUser } = useAuth(); // <- vigtigt for rolle/refresh
  const [tasks, setTasks] = useState([]);
  const [bids, setBids] = useState([]);
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ---------- helpers ----------
  const mergeTask = (prevTask, nextTask, opts = {}) => {
    const lean = !!opts.lean;
    if (!prevTask) return nextTask;
    if (!lean) return { ...prevTask, ...nextTask };
    const merged = { ...prevTask, ...nextTask };

    let preservedProofs = false;
    if ('proofs' in prevTask) {
      const prevProofs = Array.isArray(prevTask.proofs) ? prevTask.proofs : [];
      const nextProofs = Array.isArray(nextTask?.proofs) ? nextTask.proofs : null;
      if (!nextProofs || nextProofs.length < prevProofs.length) {
        merged.proofs = prevTask.proofs;
        preservedProofs = true;
      }
    }

    let preservedStatus = false;
    if ('status_history' in prevTask) {
      const prevStatus = Array.isArray(prevTask.status_history) ? prevTask.status_history : [];
      const nextStatus = Array.isArray(nextTask?.status_history) ? nextTask.status_history : null;
      if (!nextStatus || nextStatus.length < prevStatus.length) {
        merged.status_history = prevTask.status_history;
        preservedStatus = true;
      }
    }

    let preservedThumb = false;
    if (prevTask.thumbnail_url && !nextTask?.thumbnail_url) {
      merged.thumbnail_url = prevTask.thumbnail_url;
      preservedThumb = true;
    }

    if (process.env.NODE_ENV !== 'production' && (preservedProofs || preservedStatus || preservedThumb)) {
      console.debug('DEBUG:tasks lean merge preserved', {
        taskId: prevTask.id,
        proofs: preservedProofs,
        status_history: preservedStatus,
        thumbnail: preservedThumb,
      });
    }

    return merged;
  };

  const upsertTask = useCallback((updated) => {
    setTasks((prev) => {
      const i = prev.findIndex((t) => t.id === updated.id);
      if (i === -1) return [updated, ...prev];
      const copy = prev.slice();
      copy[i] = mergeTask(copy[i], updated, { lean: false });
      return copy;
    });
  }, []);

  // Hent nuværende bruger fra backend (ikke localStorage)
  const getCurrentUser = useCallback(async () => {
    try {
      const me = await api.auth.profile();
      return me || null;
    } catch {
      return null;
    }
  }, []);

  // -------- fetchers / refreshers --------
  const refreshTasks = useCallback(async () => {
    const t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    try {
      setLoading(true);
      setError(null);
      const tasksData = await api.tasks.list({ lean: true }); // rolle-baseret i backend (lean for lettere payload)
      setTasks((prev) => {
        const list = Array.isArray(tasksData) ? tasksData : [];
        return list.map((t) => {
          const existing = prev.find((p) => p.id === t.id);
          return mergeTask(existing, t, { lean: true });
        });
      });
      if (process.env.NODE_ENV !== 'production') {
        console.debug('DEBUG:tasks merge lean update', { count: Array.isArray(tasksData) ? tasksData.length : 0 });
      }
    } catch (e) {
      setTasks([]);
      setError('Kunne ikke opdatere opgaver');
    } finally {
      const t1 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
      const ms = Math.max(0, Math.round(t1 - t0));
      if (ms > 800) devWarn('Tasks slow fetch', `${ms}ms`);
      setLoading(false);
    }
  }, []);

  const refreshTaskById = useCallback(
    async (taskId) => {
      try {
        setError(null);
        const t = await api.tasks.get(taskId); // rolle-baseret i backend
        if (t) upsertTask(t);
        return t || null;
      } catch (e) {
        setError('Kunne ikke hente opgave');
        return null;
      }
    },
    [upsertTask]
  );

  const refreshBids = useCallback(async () => {
    try {
      setError(null);
      // TIP: hvis du i UI kun viser bids pr. task, kan du undlade denne
      if (api?.bids?.list) {
        const rows = await api.bids.list(); // rolle-baseret i backend
        setBids(Array.isArray(rows) ? rows : []);
      } else {
        setBids([]);
      }
    } catch {
      // ingen konsol-logs – bare stiltiende failover
    } finally {
      // valgfrit: mål varighed her hvis du vil
    }
  }, []);

  // -------- initial + user-dependent load --------
  useEffect(() => {
    let alive = true;

    (async () => {
      // på bruger-skift: ryd state for at undgå "lækkende" admin/carrier visninger
      setTasks([]);
      setBids([]);
      setChats([]);
      setError(null);
      try {
        setLoading(true);
        // kun fetch hvis der ER en bruger (server filtrerer efter rolle)
        if (currentUser) {
          const [t] = await Promise.all([api.tasks.list({ lean: true })]); // inkluderer rolle-baserede bids
          if (!alive) return;
          setTasks((prev) => {
            const list = Array.isArray(t) ? t : [];
            return list.map((row) => {
              const existing = prev.find((p) => p.id === row.id);
              return mergeTask(existing, row, { lean: true });
            });
          });
          // bids-list er valgfri – vi holder lokale bids via submit/accept/reject
        } else {
          // gæst
          setTasks([]);
          setBids([]);
          setChats([]);
        }
      } catch (e) {
        if (!alive) return;
        devError('Tasks init failed', e);
        setTasks([]);
        setBids([]);
        setChats([]);
        setError('Kunne ikke hente data');
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [currentUser]);

  // -------- Notifikationer / status --------
  const sendStatusNotification = async (taskId, newStatus) => {
    const task = tasks.find((t) => t.id === taskId);
    if (!task) return;


    try {
      // REPLACED_BY_AGENT
    } catch (error) {
      devError('Tasks error', error);
    }

    if (newStatus === 'completed') {
      try {
        const reqs = [
          {
            user_id: task.user_id,
            type: 'review_request',
            title: 'Giv din vurdering af carrier',
            message: 'Din opgave er afsluttet. Vil du give en vurdering?',
            data: { body: 'Din opgave er afsluttet. Vil du give en vurdering?' },
          },
        ];
        if (task.assigned_carrier_id) {
          reqs.push({
            user_id: task.assigned_carrier_id,
            type: 'review_request',
            title: 'Giv din vurdering af kunden',
            message: 'Opgaven er afsluttet. Vil du vurdere kunden?',
            data: { body: 'Opgaven er afsluttet. Vil du vurdere kunden?' },
          });
        }
        await Promise.all(reqs.map((r) => api.notifications.create({ ...r, task_id: taskId })));
      } catch (error) {
        devError('Tasks error', error);
      }
    }
  };

  // -------- Tasks --------
  const addTask = async (taskData) => {
    try {
      setError(null);
      const isSnake =
        taskData &&
        (Object.prototype.hasOwnProperty.call(taskData, 'from_address') ||
          Object.prototype.hasOwnProperty.call(taskData, 'to_address'));

      const taskToCreate = isSnake
        ? taskData
        : {
            user_id: taskData.userId,
            type: taskData.type,
            from_address: taskData.fromAddress,
            to_address: taskData.toAddress,
            description: taskData.description ?? null,
            is_test: !!taskData.isTest,
            is_guest: !!taskData.isGuest,
            contact_email: taskData.contactEmail ?? null,
            contact_phone: taskData.contactPhone ?? null,
          };

      const created = await api.tasks.create(taskToCreate);
      setTasks((prev) => [created, ...prev]);
      return created;
    } catch (error) {
      devError('Tasks error', error);
      setError('Kunne ikke oprette opgave');
      throw error;
    }
  };

  const updateTask = async (taskId, updates) => {
    try {
      setError(null);
      const isSnake = updates && Object.keys(updates).some((k) => k.includes('_'));
      const toSnake = (obj) => {
        const out = {};
        Object.keys(obj || {}).forEach((k) => {
          const snakeKey = k.replace(/([A-Z])/g, '_$1').toLowerCase();
          out[snakeKey] = obj[k];
        });
        return out;
      };
      const updatesToSend = isSnake ? updates : toSnake(updates);

      const updated = await api.tasks.update(taskId, updatesToSend);
      upsertTask(updated);
      return updated;
    } catch (error) {
      devError('Tasks error', error);
      setError('Kunne ikke opdatere opgave');
      throw error;
    }
  };

  const updateTaskStatus = async (taskId, newStatus, updates = {}) => {
    try {
      setError(null);

      const updatedFromApi = await api.tasks.updateStatus(taskId, {
        status: newStatus,
        updates,
      });

      // Opdater lokal state med den fulde task fra API'et
      upsertTask(updatedFromApi);

      if (newStatus !== 'created') {
        await sendStatusNotification(taskId, newStatus);
      }
      return updatedFromApi;
    } catch (error) {
      devError('Tasks error', error);
      setError('Kunne ikke opdatere opgavestatus');
      throw error;
    }
  };

  const addTaskProof = async (taskId, proofType, proofData) => {
    try {
      setError(null);
      const actor = await getCurrentUser();
      const resp = await api.tasks.addProof(taskId, {
        type: proofType,
        data: proofData,
        submitted_by: actor?.id || null,
      });
      // api returnerer { proof, task }
      const fullTask = resp?.task || (await api.tasks.get(taskId));
      if (fullTask) upsertTask(fullTask);
      return resp?.proof || null;
    } catch (error) {
      devError('Tasks error', error);
      setError('Kunne ikke tilføje bevis');
      throw error;
    }
  };

  // -------- Bids --------
  const submitBid = async (taskId, { price, valid_until }) => {
    try {
      setError(null);
      const me = await getCurrentUser();
      if (!me || me.role !== 'carrier') throw new Error('not_carrier');

      let list = null;
      try {
        const meFull = await api.users.getCurrent?.();
        list = meFull?.carrier_list_assignments?.[0]?.list || null;
      } catch {
        // ignorér – vi kan stadig byde uden liste
      }

      const newBid = await api.bids.create({
        taskId,
        price: Number(price),
        valid_until:
          valid_until || new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
        carrier_id: me.id,
        carrier_list_id: list?.id ?? null,
        carrier_fee_percentage: list?.feePercentage ?? null,
        carrier_fixed_fee: list?.fixedFee ?? null,
      });

      setBids((prev) => [newBid, ...prev]);

      // refresh task, så rolle-filtreret bids bliver korrekt i UI
      await refreshTaskById(taskId);
      return newBid;
    } catch (e) {
      devError('Tasks error', e);
      setError('Kunne ikke oprette bud');
      throw e;
    }
  };

  const updateBid = async (bidId, updates) => {
    try {
      setError(null);
      const updated = await api.bids.update(bidId, updates);
      setBids((prev) => prev.map((b) => (b.id === bidId ? updated : b)));

      // opdater også tasken der indeholder bid’et
      await refreshTaskById(updated.task_id);
      return updated;
    } catch (error) {
      devError('Tasks error', error);
      setError('Kunne ikke opdatere bud');
      throw error;
    }
  };

  const acceptBid = async (bidId) => {
    try {
      setError(null);
      // Hent biddet for at kende task_id og carrier_id
      const bid = await api.bids.get(bidId);
      if (!bid) throw new Error('bid_not_found');

      // Korrekt flow: /tasks/:id/assign (kunde/admin)
      const updatedTask = await api.tasks.assign(bid.task_id, {
        carrier_id: bid.carrier_id,
        bid_id: bidId,
      });

      // Opdater lokal state med den rolle-filtrerede task
      setTasks((prev) => {
        const i = prev.findIndex((t) => t.id === updatedTask.id);
        if (i === -1) return [updatedTask, ...prev];
        const copy = prev.slice();
        copy[i] = updatedTask;
        return copy;
      });

      // (valgfrit) hold en “bids”-cache i sync
      setBids((prev) => prev.map((b) => (b.id === bidId ? { ...b, status: 'accepted' } : b)));

      return updatedTask;
    } catch (error) {
      devError('Tasks error', error);
      setError('Kunne ikke acceptere bud');
      throw error;
    }
  };

  const rejectBid = async (bidId) => {
    try {
      setError(null);
      const rejectedBid = await api.bids.update(bidId, { status: 'rejected' });
      setBids((prev) => prev.map((b) => (b.id === bidId ? rejectedBid : b)));
      if (rejectedBid) await refreshTaskById(rejectedBid.task_id);
      return rejectedBid;
    } catch (error) {
      devError('Tasks error', error);
      setError('Kunne ikke afvise bud');
      throw error;
    }
  };

  // -------- Chat --------
  const getChatMessages = useCallback(async (taskId, user1, user2) => {
    try {
      const msgs = await api.chat.listThread(taskId, user1, user2); // kun tråd
      // sortér defensivt
      return (msgs || []).slice().sort(
        (a, b) =>
          new Date(a.created_at || a.createdAt) - new Date(b.created_at || b.createdAt)
      );
    } catch (e) {
      if (e?.response?.status === 401) return [];
      throw e;
    }
  }, []);

  const addChatMessage = useCallback(async ({ taskId, senderId, receiverId, message }) => {
    try {
      setError(null);
      const saved = await api.chat.sendMessage(taskId, receiverId, message);
      setChats((prev) => [saved, ...prev]);
      return saved;
    } catch (error) {
      devError('Tasks error', error);
      setError('Kunne ikke sende besked');
      throw error;
    }
  }, []);

  const markChatAsRead = useCallback(async (chatId) => {
    try {
      setError(null);
      await api.chat.markRead(chatId);
      setChats((prev) =>
        prev.map((c) => (c.id === chatId ? { ...c, is_read: true } : c))
      );
    } catch (error) {
      devError('Tasks error', error);
      setError('Kunne ikke markere besked som læst');
      throw error;
    }
  }, []);

  // Mulighed for at anmode admin om godkendelse af link i chat
  const requestChatLinkApproval = async ({ taskId, senderId, receiverId, urls = [], originalMessage = '' }) => {
    try {
      setError(null);
      const safeUrls = Array.isArray(urls) ? urls.slice(0, 5) : [];
      const title = `Anmodning om godkendelse af link i chat (opgave #${taskId})`;
      // Find admin-brugere og send notifikation til hver admin (kræver camelCase userId)
      let admins = [];
      try {
        admins = await api.users.list({ role: 'admin' });
      } catch {}
      admins = Array.isArray(admins) ? admins : [];
      if (admins.length === 0) throw new Error('no_admin_recipients');
      const message = originalMessage || title;

      await Promise.all(
        admins.map((a) =>
          api.notifications.create({
            userId: a.id,
            taskId: taskId,
            type: 'chat_link_approval_request',
            title,
            message: message,
            data: {
              for_admin: true,
              taskId: taskId,
              senderId: senderId,
              receiverId: receiverId,
              urls: safeUrls,
              originalMessage,
            },
          })
        )
      );

      // Bekræftelse til afsender
      try {
        await api.notifications.create({
          userId: senderId,
          taskId: taskId,
          type: 'chat_link_approval_sent',
          title: 'Din anmodning om godkendelse er sendt',
          message: 'Administrator er informeret og vil gennemgå linket snarest.',
          data: { taskId: taskId, urls: safeUrls },
        });
      } catch {}
      return { ok: true };
    } catch (error) {
      devError('Tasks error', error);
      setError('Kunne ikke sende anmodning');
      throw error;
    }
  };

  // -------- Selectors --------
  const getTasksByUser = (uid) => tasks.filter((t) => t.user_id === uid);
  const getBidsForTask = (taskId) => bids.filter((b) => b.task_id === taskId);
  const getBidsByCarrier = (carrierId) => bids.filter((b) => b.carrier_id === carrierId);
  const getUnreadMessagesCount = (uid) =>
    chats.filter((c) => c.receiver_id === uid && !c.is_read).length;

  const getCarrierFeesForBid = (bid) => {
    if (
      bid.carrier_fee_percentage !== undefined &&
      bid.carrier_fixed_fee !== undefined
    ) {
      return {
        feePercentage: bid.carrier_fee_percentage,
        fixedFee: bid.carrier_fixed_fee,
      };
    }
    const list = bid?.carrier?.carrier_list_assignments?.[0]?.list;
    if (list) return { feePercentage: list.feePercentage, fixedFee: list.fixedFee };
    return { feePercentage: 10, fixedFee: 0 };
  };

  const value = {
    tasks,
    bids,
    chats,
    loading,
    error,
    // tasks
    addTask,
    updateTask,
    updateTaskStatus,
    addTaskProof,
    refreshTasks,
    refreshTaskById,
    // bids
    submitBid,
    updateBid,
    acceptBid,
    rejectBid,
    refreshBids,
    // chat
    addChatMessage,
    getChatMessages,
    markChatAsRead,
    requestChatLinkApproval,
    // selectors
    getTasksByUser,
    getBidsForTask,
    getBidsByCarrier,
    getUnreadMessagesCount,
    getCarrierFeesForBid,
  };

  return <TaskContext.Provider value={value}>{children}</TaskContext.Provider>;
};
